import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';

import { Tooltip } from 'common/components';

import { useBowser, useI18n } from 'common/hooks';
import { useCustomer } from 'common/hooks/useCustomer/useCustomer.hook';
import svgIcons from 'common/svgIcons';

import { routes } from 'routes/routes.constants';

import { memoizedComponent } from 'utils/memoizeComponent';

import { useTheme } from 'blocks/ThemeProvider';

import { AuthProtectedLink } from '../AuthProtectedLink/AuthProtectedLink.component';
import SvgIconUrl from '../SvgIconUrl/SvgIconUrl.component';
import './UserTier.css';
import { Tiers } from './types';

type Props = {
  className?: string;
};

export const UserTier: React.FC<React.PropsWithChildren<Props>> = memoizedComponent(({ className = '' }) => {
  const { translate } = useI18n();
  const { isDesktop } = useBowser();
  const { tier, tiers, isAuthenticated } = useCustomer();
  const { layout } = useTheme();

  const { currentTier, totalExperiencePoints = 0 } = tier;

  const nextTier = useMemo(
    () => tiers.find((i, index) => i && tiers[index - 1] && tiers[index - 1].name === currentTier),
    [currentTier, tiers],
  );

  const neadedPoints = useMemo(
    () => (Number(nextTier?.experienceRangeFrom) - totalExperiencePoints).toLocaleString(),
    [nextTier?.experienceRangeFrom, totalExperiencePoints],
  );

  const tierClassNames = classNames({
    tier: true,
    tier__transparent: layout.sidebar.compact && isDesktop,
    tier__silver: currentTier === Tiers.SILVER,
    tier__gold: currentTier === Tiers.GOLD,
    tier__platinum: currentTier === Tiers.PLATINUM,
    tier__diamond: currentTier === Tiers.DIAMOND,
    'tier__black-diamond': currentTier === Tiers.DIAMOND_BLACK,
    [className]: className,
  });

  const renderStatistics = useCallback(() => {
    const progress = (100 * totalExperiencePoints) / Number(nextTier?.experienceRangeFrom);
    return (
      <div className='tier__statistics'>
        <div className='tier__statistics_current'>
          <p data-test='user-tier' className='tier__statistics_current-title'>
            {currentTier}
          </p>
          {isDesktop && <SvgIconUrl className='tier__statistics_current-arrow' symbol={svgIcons.ArrowRight()} />}
          <p data-test='user-xp' className='tier__statistics_current-value'>
            {Number(totalExperiencePoints).toLocaleString()} XP
          </p>
        </div>
        {nextTier && isDesktop && (
          <div data-test='user-tier-progress' className='tier__progress_slider'>
            <div style={{ width: `${progress}%` }} className='tier__progress_slider-value' />
          </div>
        )}
        {nextTier && (
          <p data-test='user-tier-next' className='tier__target'>
            {translate
              .toText('mobile:tier:points-needed')
              .replace('{points}', neadedPoints)
              .replace('{tier}', String(nextTier.name))}
          </p>
        )}
      </div>
    );
  }, [currentTier, isDesktop, neadedPoints, nextTier, totalExperiencePoints, translate]);

  const renderTierNotExist = useCallback(
    () => (
      <div data-test='user-tier-empty' className='tier__start'>
        <p className='tier__start-title'>{translate.toText('mobile:tier:start')}</p>
        <SvgIconUrl className='tier__start-icon' symbol={svgIcons.ArrowRight()} />
      </div>
    ),
    [translate],
  );

  const renderUserTier = useCallback(() => {
    if (!currentTier) return renderTierNotExist();
    return renderStatistics();
  }, [currentTier, renderStatistics, renderTierNotExist]);

  const renderUserTierIcon = useCallback(() => {
    let icon = 'tier-silver';
    switch (currentTier) {
      case Tiers.PLATINUM:
        icon = 'tier-platinum';
        break;
      case Tiers.GOLD:
        icon = 'tier-gold';
        break;
      case Tiers.DIAMOND:
        icon = 'tier-diamond';
        break;
      case Tiers.DIAMOND_BLACK:
        icon = 'tier-black-diamond';
        break;

      default:
        break;
    }
    return <div className={`tier__icon ${icon}`} />;
  }, [currentTier]);

  const userTierTooltip = useMemo(
    () => (
      <>
        <div className='Body3_reg_16'>{Number(totalExperiencePoints).toLocaleString()} XP</div>
        {nextTier && (
          <div className='Body2_reg_14'>
            {translate
              .toText('mobile:tier:points-needed')
              .replace('{points}', neadedPoints)
              .replace('{tier}', String(nextTier.name))}
          </div>
        )}
      </>
    ),
    [neadedPoints, nextTier, totalExperiencePoints, translate],
  );

  return (
    <Tooltip enabled={!!(layout.sidebar.compact && isAuthenticated)} overlay={userTierTooltip} placement='right'>
      <AuthProtectedLink to={routes.getLoyaltyPage()} className={tierClassNames}>
        {renderUserTierIcon()}
        {renderUserTier()}
      </AuthProtectedLink>
    </Tooltip>
  );
});
